import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Spinner,
  VStack,
  Text,
  useColorMode,
  Box,
  IconButton,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
} from "@chakra-ui/react";
import { FiMenu } from "react-icons/fi";
import { IconWrapper } from "../../components/IconWrapper";
import { getThemeClasses } from "../../utils/themeUtils";
import { SectionNode } from "./types";
import HelpSidebar from "./HelpSidebar";
import HelpSection from "./HelpSection";

const Help: React.FC = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  const [sections, setSections] = useState<SectionNode[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string>("");
  const [targetChildId, setTargetChildId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [rawHtml, setRawHtml] = useState<string>("");

  const prefetchIndexRef = useRef(1);
  const sectionsRef = useRef<SectionNode[]>([]);

  // Keep sectionsRef in sync
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  // Process HTML to clean up content
  const processHtml = useCallback((raw: string): string => {
    let result = raw;
    const firstRealH1 = result.indexOf('<h1 class="c86 c19" id="h.');
    if (firstRealH1 > 0) {
      result = result.substring(firstRealH1);
    }
    result = result.replace(
      /<span[^>]*>Table of Contents<\/span>[\s\S]*?(?=<hr[^>]*>|<h1)/i,
      ""
    );
    result = result.replace(/<h1[^>]*><span[^>]*><\/span><\/h1>/gi, "");
    result = result.replace(/<h2[^>]*><span[^>]*><\/span><\/h2>/gi, "");
    result = result.replace(/<h3[^>]*><span[^>]*><\/span><\/h3>/gi, "");
    result = result.replace(
      /(<\/h1>)(<p[^>]*><span[^>]*>Overview<\/span><\/p>)/gi,
      '$1<h2 class="c25 c19"><span class="c17">Overview</span></h2>'
    );
    return result;
  }, []);

  // Parse HTML into sections based on h1 and h2 tags
  const parseSections = useCallback(
    (html: string): SectionNode[] => {
      const result: SectionNode[] = [];

      // Find all h1 opening tags with their positions
      const h1OpenRegex = /<h1[^>]*>/gi;
      const h1CloseRegex = /<\/h1>/gi;
      const h2OpenRegex = /<h2[^>]*>/gi;
      const h2CloseRegex = /<\/h2>/gi;

      const h1Opens: { pos: number; full: string }[] = [];
      let m: RegExpExecArray | null;
      while ((m = h1OpenRegex.exec(html)) !== null) {
        h1Opens.push({ pos: m.index, full: m[0] });
      }

      // For each h1, find its closing tag and content
      for (let i = 0; i < h1Opens.length; i++) {
        const h1Start = h1Opens[i].pos;
        const h1OpenTag = h1Opens[i].full;
        const contentStart = h1Start + h1OpenTag.length;

        // Find closing </h1> after this opening tag
        h1CloseRegex.lastIndex = contentStart;
        const h1CloseMatch = h1CloseRegex.exec(html);
        if (!h1CloseMatch) continue;
        const h1End = h1CloseRegex.lastIndex;

        // Content between h1 opening and closing tags
        const h1Inner = html.substring(contentStart, h1CloseMatch.index);

        // Extract h1 title text (strip HTML tags)
        const title = h1Inner.replace(/<[^>]*>/g, "").trim() || `Section ${i + 1}`;

        // Extract h1 id
        const idMatch = h1OpenTag.match(/id="([^"]*)"/);
        const id = idMatch ? idMatch[1] : `section-${i + 1}`;

        // Content after </h1> until next <h1> or end
        const nextH1Start = i + 1 < h1Opens.length ? h1Opens[i + 1].pos : html.length;
        const sectionBody = html.substring(h1End, nextH1Start);

        // Find h2 tags within this section body
        const h2Opens: { pos: number; full: string }[] = [];
        h2OpenRegex.lastIndex = 0;
        let h2m: RegExpExecArray | null;
        while ((h2m = h2OpenRegex.exec(sectionBody)) !== null) {
          h2Opens.push({ pos: h2m.index, full: h2m[0] });
        }

        // Content includes the full section body so all subsections are shown when the main header is selected
        const h1Content = `<h1${h1OpenTag.substring(3)}${h1Inner}</h1>${sectionBody}`;

        const section: SectionNode = {
          id,
          title,
          level: 1,
          content: h1Content,
          children: [],
          loaded: false,
          loading: false,
        };

        // Process h2 children
        for (let j = 0; j < h2Opens.length; j++) {
          const h2Start = h2Opens[j].pos;
          const h2OpenTag = h2Opens[j].full;
          const h2ContentStart = h2Start + h2OpenTag.length;

          // Find closing </h2>
          h2CloseRegex.lastIndex = h2ContentStart;
          const h2CloseMatch = h2CloseRegex.exec(sectionBody);
          if (!h2CloseMatch) continue;

          const h2Inner = sectionBody.substring(h2ContentStart, h2CloseMatch.index);
          const h2End = h2CloseRegex.lastIndex;

          // Extract h2 title
          const h2Title = h2Inner.replace(/<[^>]*>/g, "").trim() || `Subsection ${j + 1}`;

          // Extract h2 id
          const h2IdMatch = h2OpenTag.match(/id="([^"]*)"/);
          const h2Id = h2IdMatch ? h2IdMatch[1] : `${id}-h2-${j + 1}`;

          // Content after </h2> until next <h2> or end of section
          const nextH2Start = j + 1 < h2Opens.length ? h2Opens[j + 1].pos : sectionBody.length;
          const h2Content = `<h2${h2OpenTag.substring(3)}${h2Inner}</h2>${sectionBody.substring(h2End, nextH2Start)}`;

          section.children.push({
            id: h2Id,
            title: h2Title,
            level: 2,
            content: h2Content,
            children: [],
            loaded: false,
            loading: false,
          });
        }

        result.push(section);
      }

      return result;
    },
    []
  );

  // Load a specific section
  const loadSection = useCallback(
    (index: number) => {
      setSections((prev) => {
        const updated = [...prev];
        if (index >= updated.length) return prev;
        if (updated[index].loaded) return prev;

        updated[index] = {
          ...updated[index],
          loading: true,
        };
        return updated;
      });

      // Simulate loading delay and mark as loaded
      setTimeout(() => {
        setSections((prev) => {
          const updated = [...prev];
          if (index >= updated.length) return prev;
          updated[index] = {
            ...updated[index],
            loaded: true,
            loading: false,
          };
          return updated;
        });
      }, 100 + index * 50);
    },
    []
  );

  // Load a specific section immediately (for user clicks)
  const loadSectionImmediate = useCallback(
    (sectionId: string) => {
      setSections((prev) => {
        const updated = [...prev];
        const index = updated.findIndex((s) => s.id === sectionId);
        if (index === -1) return prev;
        if (updated[index].loaded) return prev;

        updated[index] = {
          ...updated[index],
          loading: true,
        };
        return updated;
      });

      setTimeout(() => {
        setSections((prev) => {
          const updated = [...prev];
          const index = updated.findIndex((s) => s.id === sectionId);
          if (index === -1) return prev;
          updated[index] = {
            ...updated[index],
            loaded: true,
            loading: false,
          };
          return updated;
        });
      }, 50);
    },
    []
  );

  // Fetch HTML and initialize sections
  useEffect(() => {
    const fetchHtml = async () => {
      try {
        const response = await fetch("/Help.html");
        const text = await response.text();
        const bodyMatch = text.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
        const content = bodyMatch ? bodyMatch[1] : text;
        const cleaned = content.replace(/<style[\s\S]*?<\/style>/gi, "");
        const withAbsolutePaths = cleaned.replace(
          /src='images\//g,
          "src='/images/"
        );
        const processed = processHtml(withAbsolutePaths);
        setRawHtml(processed);

        const parsed = parseSections(processed);
        setSections(parsed);

        if (parsed.length > 0) {
          setActiveSectionId(parsed[0].id);
          // Mark first section as loaded
          parsed[0].loaded = true;
          setSections([...parsed]);
        }
      } catch (error) {
        console.error("Failed to load help documentation:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchHtml();
  }, [processHtml, parseSections]);

  // Prefetch subsequent sections after first load
  useEffect(() => {
    if (sections.length === 0 || initialLoading) return;

    // Find first unloaded section index
    const startIdx = sections.findIndex((s) => !s.loaded);
    if (startIdx === -1) return; // All loaded

    prefetchIndexRef.current = startIdx;

    const prefetchInterval = setInterval(() => {
      if (prefetchIndexRef.current >= sectionsRef.current.length) {
        clearInterval(prefetchInterval);
        return;
      }

      const idx = prefetchIndexRef.current;
      const currentSections = sectionsRef.current;
      if (!currentSections[idx].loaded && !currentSections[idx].loading) {
        loadSection(idx);
      }
      prefetchIndexRef.current++;
    }, 400);

    return () => clearInterval(prefetchInterval);
  }, [sections.length, initialLoading, loadSection]);

  // Handle section selection
  const handleSelectSection = useCallback(
    (sectionId: string, childId?: string) => {
      if (childId) {
        setActiveSectionId(childId);
        setTargetChildId(childId);
      } else {
        setActiveSectionId(sectionId);
        setTargetChildId(null);
      }

      // If parent section not loaded, load it immediately
      const section = sections.find(
        (s) => s.id === sectionId || s.children.some((c) => c.id === sectionId)
      );
      if (section && !section.loaded) {
        loadSectionImmediate(section.id);
      }
    },
    [sections, loadSectionImmediate]
  );

  // Find active parent section to render
  const activeSection = sections.find(
    (s) =>
      s.id === activeSectionId || s.children.some((c) => c.id === activeSectionId)
  );

  // Get active parent section details for breadcrumbs
  const activeParent = sections.find(
    (s) =>
      s.id === activeSectionId || s.children.some((c) => c.id === activeSectionId)
  );

  const activeChild = activeParent?.children.find((c) => c.id === activeSectionId);

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 32px)" }}>
      {/* Module Header */}
      <div
        className={`flex-shrink-0 flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}
      >
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <div className="flex items-center space-x-3">
          {/* Mobile Navigation Toggle Button */}
          <IconButton
            display={{ base: "flex", md: "none" }}
            aria-label="Open sidebar"
            icon={<IconWrapper icon={FiMenu} size={16} />}
            size="sm"
            variant="outline"
            borderColor="#B59143"
            color="#FBD282"
            _hover={{ bg: isDark ? "#14161B" : "#F9FAFB" }}
            onClick={() => setIsMobileDrawerOpen(true)}
          />

          <div className="flex flex-col space-y-1">
            <span className={`text-2xl font-bold ${theme.text}`}>
              Help & Documentation
            </span>
            <span className={`text-xs ${theme.textMuted}`}>
              ZenSight User Guide
            </span>
          </div>
        </div>
      </div>

      {/* Content Area with Sidebar */}
      <div
        className={`${theme.card} border ${theme.border} rounded-md overflow-hidden`}
        style={{ flex: 1, display: "flex" }}
      >
        {initialLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <VStack spacing={4}>
              <Spinner size="xl" color="zs.accent" />
              <Text color={theme.textMuted}>Loading documentation...</Text>
            </VStack>
          </div>
        ) : sections.length > 0 ? (
          <>
            {/* Desktop Sidebar (Hidden on base mobile screens) */}
            <Box display={{ base: "none", md: "block" }}>
              <HelpSidebar
                sections={sections}
                activeSectionId={activeSectionId}
                onSelectSection={handleSelectSection}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isDark={isDark}
              />
            </Box>

            {/* Mobile Sidebar Drawer */}
            <Drawer
              isOpen={isMobileDrawerOpen}
              placement="left"
              onClose={() => setIsMobileDrawerOpen(false)}
            >
              <DrawerOverlay />
              <DrawerContent bg={isDark ? "#0B0B0F" : "#FFFFFF"} maxW="280px">
                <DrawerCloseButton
                  color={isDark ? "#FFFFFF" : "#1A202C"}
                  style={{ top: "12px", right: "12px", zIndex: 10 }}
                />
                <HelpSidebar
                  sections={sections}
                  activeSectionId={activeSectionId}
                  onSelectSection={(secId, chId) => {
                    handleSelectSection(secId, chId);
                    setIsMobileDrawerOpen(false);
                  }}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  isDark={isDark}
                />
              </DrawerContent>
            </Drawer>

            {/* Section Content Pane */}
            <div
              className="flex-1 overflow-auto p-6 scroll-smooth custom-scrollbar"
              style={{ minWidth: 0 }}
            >
              {activeSection ? (
                <HelpSection
                  section={activeSection}
                  isDark={isDark}
                  theme={theme}
                  targetChildId={targetChildId}
                  searchQuery={searchQuery}
                  parentTitle={activeChild ? activeParent?.title : undefined}
                />
              ) : (
                <VStack py={20} spacing={4} flex={1} justify="center">
                  <Text color={theme.textMuted}>Select a section from the sidebar.</Text>
                </VStack>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <VStack spacing={4}>
              <Text color={theme.textMuted}>
                Failed to load help documentation.
              </Text>
            </VStack>
          </div>
        )}
      </div>
    </div>
  );
};

export default Help;
